import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  { title: "Basic details", fields: [["full_name", "Full name"], ["gender", "Gender"], ["date_of_birth", "Date of birth", "date"], ["mobile", "Mobile"], ["marital_status", "Marital status"], ["height_cm", "Height (cm)", "number"]] },
  { title: "Community", fields: [["religion", "Religion"], ["caste", "Community / Caste"], ["mother_tongue", "Mother tongue"]] },
  { title: "Education & profession", fields: [["education", "Education"], ["profession", "Profession"], ["company", "Company"], ["annual_income_gbp", "Annual income (£)", "number"]] },
  { title: "Current residence", fields: [["residence_country", "Residence country"], ["current_city", "City"], ["current_postcode", "Postcode"], ["current_address", "Address"]] },
  { title: "Home country & family", fields: [["home_country", "Home country"], ["home_country_city", "Home city"], ["home_country_address", "Home address"], ["father_name", "Father's name"], ["father_occupation", "Father's occupation"], ["mother_name", "Mother's name"], ["mother_occupation", "Mother's occupation"], ["parents_phone", "Parents' phone"]] },
  { title: "Immigration", fields: [["visa_type", "Visa type"], ["visa_expiry_date", "Visa expiry", "date"], ["visa_share_code", "Visa share code"], ["passport_number", "Passport number"], ["passport_issue_country", "Passport issue country"], ["citizenship_certificate_number", "Citizenship certificate no."]] },
];
const LONG = [["about_me", "About me"], ["partner_preference", "Partner preference"]];
const ALL_KEYS = [...SECTIONS.flatMap((s) => s.fields.map((f) => f[0])), ...LONG.map((f) => f[0])];

function parseISO(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s || "").trim());
  if (!m) return null;
  const y = +m[1], mo = +m[2], da = +m[3];
  const d = new Date(y, mo - 1, da);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== da) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}
function ageFrom(d) {
  const n = new Date();
  let a = n.getFullYear() - d.getFullYear();
  const mm = n.getMonth() - d.getMonth();
  if (mm < 0 || (mm === 0 && n.getDate() < d.getDate())) a--;
  return a;
}
function validateField(k, v) {
  const s = (v ?? "").toString().trim();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  switch (k) {
    case "full_name": return s ? "" : "Full name is required";
    case "mobile": return !s ? "" : (/^\+?[0-9][0-9 ()\-]{6,19}$/.test(s) ? "" : "Enter a valid phone number");
    case "height_cm": { if (!s) return ""; const n = Number(s); return n >= 120 && n <= 250 ? "" : "Height must be 120–250 cm"; }
    case "annual_income_gbp": { if (!s) return ""; const n = Number(s); return !isNaN(n) && n >= 0 ? "" : "Enter a valid amount"; }
    case "date_of_birth": {
      if (!s) return "";
      const d = parseISO(s); if (!d) return "Use date format YYYY-MM-DD";
      if (d > today) return "Date of birth can't be in the future";
      const a = ageFrom(d); if (a < 18) return "You must be at least 18"; if (a > 100) return "Enter a valid date of birth";
      return "";
    }
    case "visa_expiry_date": {
      if (!s) return "";
      const d = parseISO(s); if (!d) return "Use date format YYYY-MM-DD";
      if (d < today) return "Visa has expired — enter a future date";
      return "";
    }
    default: return "";
  }
}

export default function EditProfile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(() => ({ ...(user || {}) }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  if (!user) return null;
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: validateField(k, v) }));
  };

  const save = async () => {
    const errs = {};
    ALL_KEYS.forEach((k) => { const msg = validateField(k, form[k]); if (msg) errs[k] = msg; });
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fix the highlighted fields"); return; }
    setSaving(true);
    try {
      const payload = {};
      ALL_KEYS.forEach((k) => { if (form[k] !== undefined && form[k] !== null) payload[k] = form[k]; });
      ["height_cm", "annual_income_gbp"].forEach((k) => { if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]); });
      await api.post("/profiles/update", payload);
      await refresh();
      toast.success("Profile updated");
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="edit-profile-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Edit your profile</h1>
          <p className="text-slate-600 mt-1">Update any detail below. Your email address can't be changed.</p>
        </div>
        <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-full">Cancel</Button>
      </div>

      <Card className="p-6 border-[#EAE3D2] mt-6">
        <label className="text-xs uppercase tracking-wider text-slate-500">Email (can't change)</label>
        <Input value={user.email || ""} disabled className="mt-1 bg-slate-50" data-testid="edit-email-readonly" />
      </Card>

      {SECTIONS.map((sec) => (
        <Card key={sec.title} className="p-6 border-[#EAE3D2] mt-5">
          <h3 className="font-serif text-xl text-slate-900 mb-4">{sec.title}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sec.fields.map(([k, label, type]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-wider text-slate-500">{label}</label>
                <Input
                  type={type || "text"}
                  value={form[k] ?? ""}
                  onChange={(e) => set(k, e.target.value)}
                  data-testid={`edit-${k}`}
                  className={`mt-1 ${errors[k] ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                />
                {errors[k] && <p className="text-xs text-red-600 mt-1" data-testid={`error-${k}`}>{errors[k]}</p>}
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card className="p-6 border-[#EAE3D2] mt-5">
        <h3 className="font-serif text-xl text-slate-900 mb-4">About</h3>
        <div className="space-y-4">
          {LONG.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-wider text-slate-500">{label}</label>
              <Textarea rows={4} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`edit-${k}`} className="mt-1" />
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-3 mt-6">
        <Button onClick={save} disabled={saving} data-testid="edit-save-btn" className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-8">
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}
